/**
 * Live "gift progress" arrow list.
 *
 * The server renders the reached/next states and the remaining amounts for the
 * current cart (see `snippets/gift-progress-bar.liquid`), so the first paint is
 * always right. This module keeps it in sync after AJAX cart changes (add to cart,
 * quantity update) — the announcement bar lives in the header and is NOT re-rendered
 * on `cart:update`, so it must be patched client-side (the cart drawer morphs
 * server-side, and this keeps it consistent too).
 *
 * Each `.gift-progress__node[data-threshold]` shows "zbývá <amount>" until reached.
 * Money is mirrored from two server `| money` samples on the container, so the live
 * "zbývá X" amounts match the shop locale exactly (Shopify localizes separators per
 * shop — e.g. a space for cs-CZ — which the placeholder helper does not replicate).
 */
(function () {
  const SELECTOR = '[data-gift-progress]';

  function detectFormat(el) {
    const keep = /[^\d.,\s']/g;
    const grpRaw = el.dataset.moneyGrp || '';
    const decRaw = el.dataset.moneyDec || '';
    const grpNum = grpRaw.replace(keep, '').trim();
    const decNum = decRaw.replace(keep, '').trim();

    let decimals = 0;
    let decSep = '';
    const dec = decNum.match(/^\d+(\D)(\d+)$/);
    if (dec) {
      decSep = dec[1];
      decimals = dec[2].length;
    }

    let grpInt = grpNum;
    if (decimals > 0 && grpNum.length > decimals + 1) {
      grpInt = grpNum.slice(0, grpNum.length - decimals - 1);
    }
    const grp = grpInt.match(/\d+(\D)\d{3}/);
    const groupSep = grp ? grp[1] : '';

    // Symbol + jeho umístění (prefix/suffix) se odvodí přímo z `data-money-dec` — ten jde
    // přes `| money`, a je tedy presentment-aware: cokoli kolem číselné části toho vzorku
    // (symbol, mezera, měnový kód) je přesně to, co patří AKTUÁLNÍ měně košíku, ať je to
    // CZK, EUR nebo cokoli dalšího. Díky tomu není potřeba posílat do DOM zvlášť
    // formátovací řetězec.
    //
    // OPRAVA KOMENTÁŘE 2026-09-14: dřív tu stálo, že `shop.money_format` je natvrdo formát
    // obchodu a na SK by přepsal "€40,00" na "40,00 Kč". NENÍ to pravda — Liquid
    // `{{ shop.money_format }}` se na storefrontu řídí trhem požadavku (změřeno na royalbay:
    // `royalbay.cz` → "{{amount_with_comma_separator}} Kč", `royalbay.sk` i
    // `royalbay.cz/?country=SK` → "€{{amount_with_comma_separator}}"). Formát OBCHODU vrací
    // Admin API, ne Liquid. Obě cesty fungují; tahle je jen o atribut kratší.
    // Viz `docs/audit-doprava-zdarma-sk-2026-09-14.md` §7.
    let prefix = '';
    let suffix = '';
    const digits = decRaw.match(/\d(?:[\d.,\s']*\d)?/);
    if (digits) {
      prefix = decRaw.slice(0, digits.index);
      suffix = decRaw.slice(digits.index + digits[0].length);
    }

    return { decimals, decSep, groupSep, prefix, suffix };
  }

  function formatMoney(cents, el) {
    const { decimals, decSep, groupSep, prefix, suffix } = detectFormat(el);
    const value = (cents / 100).toFixed(decimals);
    let [intPart, decPart] = value.split('.');
    if (groupSep) intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSep);
    const number = decimals > 0 ? intPart + decSep + decPart : intPart;
    return prefix + number + suffix;
  }

  /**
   * Recompute one gift list for the given cart total.
   * @param {HTMLElement} container
   * @param {number} totalCents
   */
  function updateOne(container, totalCents) {
    const word = container.dataset.remainingWord || '';
    const nodes = container.querySelectorAll('.gift-progress__node[data-threshold]');

    let nextRemaining = Infinity;
    let nextNode = null;
    nodes.forEach(function (node) {
      const threshold = parseInt(node.dataset.threshold || '0', 10);
      const reached = totalCents >= threshold;
      node.classList.toggle('is-reached', reached);
      const remainingEl = node.querySelector('[data-remaining]');
      if (reached) {
        if (remainingEl) remainingEl.textContent = '';
      } else {
        const remaining = threshold - totalCents;
        if (remainingEl) remainingEl.textContent = word + ' ' + formatMoney(remaining, container);
        if (remaining < nextRemaining) {
          nextRemaining = remaining;
          nextNode = node;
        }
      }
    });
    nodes.forEach(function (node) {
      node.classList.toggle('is-next', node === nextNode);
    });
  }

  function updateAll(totalCents) {
    document.querySelectorAll(SELECTOR).forEach(function (container) {
      updateOne(container, totalCents);
    });
  }

  /** Sníží eligible útratu o podíl CART-LEVEL slev (2026-09-12).
   *
   * `final_line_price` zahrnuje jen LINE-LEVEL slevy (`allocation_method` `each`/`entitled`).
   * Sleva na celý košík (`across`, typicky „−50 % na všechno") se na řádky NEALOKUJE —
   * měřeno na theme dev: kód `TEST` (−50 %) nechá `final_line_price` beze změny a projeví se
   * jen v `total_price`. Bez téhle korekce si theme myslí, že košík má pořád 1 512 Kč,
   * a nabídne dárek k útratě, která reálně je 756 Kč.
   *
   * Poměr `total_price / items_subtotal_price` je přesně podíl, který z košíku po line-level
   * slevách zbyl. Dárková BXGY sleva je line-level, takže je v `items_subtotal_price` už
   * zohledněná a do poměru se nepromítne (žádná oscilace „dárek zlevní košík → práh spadne").
   *
   * Beze slev je poměr 1 a funkce nic nemění.
   *
   * Musí dát stejné číslo jako gift-auto-add.js, tb-cart-qty.js a gift-eligible-cents.liquid.
   */
  function applyCartLevelDiscount(eligible, cart) {
    const subtotal = Number(cart && cart.items_subtotal_price) || 0;
    const total = Number(cart && cart.total_price) || 0;
    if (subtotal <= 0 || total >= subtotal) return eligible;
    return Math.floor((eligible * total) / subtotal);
  }

  /** Útrata započitatelná do prahů — musí odpovídat gift-auto-add.js i gift-eligible-cents.liquid.
   * Task 9 (2026-09-10, otáčí Task 5c): dárkové produkty (Oat Bar, láhev, Hydration…) se
   * POČÍTAJÍ do prahu jako každé jiné zboží, i outlet — theme dárek je PŘIDÁVÁ jako další
   * kus, takže placená hromádka se nezmenší. Vylučuje se jen samotný dárkový řádek
   * (`_gift_progress`) a poukazy + dosluhující mystery dárek (typy `Dárkový poukaz`/`Dárek`). */
  function eligibleCents(cart) {
    if (!cart || !cart.items) return 0;

    // Product types VYLOUČENÉ z útraty (Task 9) — NENÍ whitelist `gift{N}_variant_skus`
    // (ten dál řeší jen „kterou variantu darujeme", `snippets/gift-variant-whitelist-ids.liquid`).
    // Přednostní zdroj: `#gift-progress-excluded-types` (snippets/scripts.liquid →
    // gift-excluded-types.liquid), renderovaný VŽDY (nezávisle na gift_progress_enabled/
    // gift_auto_add_enabled — je to statický seznam dvou typů, žádná závislost na settings).
    // Hardcoded fallback jen pro rozbitý JSON (corrupt markup), stejný jako gift-auto-add.js
    // a tb-cart-qty.js — musí dát stejné číslo.
    const excludedTypes = new Set(['Dárkový poukaz', 'Dárek']);
    const idsEl = document.getElementById('gift-progress-excluded-types');
    if (idsEl) {
      try {
        const parsed = JSON.parse(idsEl.textContent);
        if (Array.isArray(parsed)) {
          excludedTypes.clear();
          parsed.forEach(function (t) {
            excludedTypes.add(String(t));
          });
        }
      } catch (e) {
        // Rozbitý JSON → zůstává hardcoded fallback výš.
      }
    }

    const eligible = cart.items.reduce(function (sum, item) {
      if (item.properties && item.properties['_gift_progress']) return sum;
      if (item.gift_card) return sum;
      if (excludedTypes.has(item.product_type)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);

    return applyCartLevelDiscount(eligible, cart);
  }

  function refreshFromCart() {
    if (document.querySelector(SELECTOR) === null) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (cart) updateAll(eligibleCents(cart));
      })
      .catch(function () {
        // Network hiccup — leave the server-rendered list in place.
      });
  }

  document.addEventListener('cart:update', refreshFromCart);
})();
